<!-- The kitchen from above. THIS FILE IS YOURS.

     The 3D view is for judging a finish; this one is for arranging. A corner in perspective
     is a place where two walls happen to overlap on screen, and nobody can drag a cabinet
     round it with a fingertip. In plan a corner is a corner, a wall is a line you can take
     hold of, and every millimetre on screen is a millimetre a maker will cut.

     Three things a finger can do here, and the rule is the same as the 3D view's -- there is
     no mode to learn:
       a wall       slides in and out, and the walls beside it follow
       a corner     moves freely, snapping to a clean angle, which is how a splay is made
       a cabinet    slides along its run
     Tapping a wall with nothing on it puts cabinets on it; tapping one with cabinets on it
     takes them off. That is the whole "shape" control: a shape is which walls carry runs.

     AND ONE MODE AFTER ALL, which earns itself: `picking`.

     A LiDAR scan of an open-plan floor comes back as the whole floor -- the owner's was
     twenty walls, kitchen and dining and lounge, one closed loop -- and laying a kitchen out
     against all of it puts cupboards along a living-room wall. Nothing in the geometry can
     tell which end is the kitchen, because nothing in the room does either. So after a scan
     the person is asked, and this is where they answer: the same room, the same walls, the
     same doorways, with every drag taken away so the only thing a finger can do is say yes
     or no to a wall.

     It is a prop and not a fork because the geometry is the whole file: the fit, the
     openings broken over the wall line, the island's overhang, the millimetre labels. A
     second copy of that to keep in step with this one is a second copy to get wrong. -->
<template>
  <div class="plan" ref="box">
    <!-- Two things a plan can show that a kitchen cannot: how much room there is to stand,
         and how far it is from here to there. Both off until asked for -- and neither is
         offered while walls are being picked, where the only question is which walls. -->
    <div v-if="!picked" class="tools" :style="{ top: `${top + 8}px` }">
      <button type="button" :class="{ on: showClear }" @click="showClear = !showClear">Clearances</button>
      <button type="button" :class="{ on: measuring }" @click="toggleMeasure">
        {{ measuring ? (points.length ? 'Done measuring' : 'Tap two points') : 'Measure' }}
      </button>
    </div>

    <svg :viewBox="`0 0 ${size.w} ${size.h}`" :width="size.w" :height="size.h"
         :class="{ measuring, picking: !!picked }"
         @pointerdown="down" @pointermove="move" @pointerup="up" @pointercancel="up"
         @pointerleave="up">
      <!-- a 500mm grid, because a room is measured -->
      <path :d="grid" class="grid" />

      <path :d="floorD" class="floor" />

      <!-- room to stand: the metre in front of every bench a person needs -->
      <g v-if="showClear" class="clear">
        <path v-for="(c, i) in clearD" :key="'k' + i" :d="c.d" />
        <text v-for="(c, i) in clearD" :key="'kt' + i" :transform="`translate(${c.lx} ${c.ly}) rotate(${c.rot})`" y="4">{{ c.label }}</text>
      </g>

      <!-- runs, as the depth they take from the room -->
      <g v-for="r in runsD" :key="r.id" :class="['run', r.kind]">
        <path :d="r.d" class="strip" />
        <g v-for="c in r.cabs" :key="c.id" :class="['cab', { on: c.id === selected?.id, held: c.id === drag?.itemId }]"
           @pointerdown.stop="grabCab($event, r, c)">
          <path :d="c.d" />
          <circle v-if="c.role === 'sink'" :cx="c.cx" :cy="c.cy" :r="c.r" class="sink" />
          <g v-if="c.role === 'cooktop'" class="hob">
            <circle v-for="(p, i) in c.rings" :key="i" :cx="p.x" :cy="p.y" :r="c.r * 0.38" />
          </g>
          <text v-if="c.letter" :x="c.cx" :y="c.cy + 4" class="letter">{{ c.letter }}</text>
          <path v-if="c.role === 'corner'" :d="c.d" class="corner" />
          <g v-if="c.locked" class="lock" :transform="`translate(${c.cx - 6} ${c.cy - 7})`">
            <rect x="1" y="6" width="10" height="8" rx="2" />
            <path d="M3 6V4a3 3 0 0 1 6 0v2" fill="none" />
          </g>
        </g>
      </g>

      <!-- An island: its outline, its seating overhang, and a grip to turn it by. Drawn
           before the runs so the cabinets sit on top of it.

           While picking it is one more thing to say yes or no to -- an island is cabinetry,
           and a scan that found a big benchtop out in the middle of an open-plan floor has
           found the one piece of it nobody has to be told about. The grip goes; there is
           nothing to turn yet. -->
      <g v-for="s in islandsD" :key="s.id" class="island" :class="{ out: s.out }">
        <path :d="s.over" class="over" />
        <path :d="s.body" class="body" @pointerdown.stop="grabIsland($event, s)" />
        <g v-if="!picked" class="spin" @pointerdown.stop="grabSpin($event, s)">
          <circle :cx="s.gx" :cy="s.gy" r="15" class="hit" />
          <circle :cx="s.gx" :cy="s.gy" r="7" class="dot" />
        </g>
      </g>

      <!-- The ends of a run, draggable. A run fills its wall until somebody pulls an end
           in: that is how a fridge alcove, a chimney breast, or a run that simply stops
           part-way along a long wall gets drawn. -->
      <g v-for="e in endsD" :key="e.key" class="runend" :class="{ hot: drag?.kind === 'span' && drag.key === e.key }"
         @pointerdown.stop="grabEnd($event, e)">
        <circle :cx="e.x" :cy="e.y" r="15" class="hit" />
        <path :d="e.d" class="bar" />
      </g>

      <!-- corner allowances, so it is visible where the corner cabinet lives -->
      <path v-for="(c, i) in cornersD" :key="'c' + i" :d="c" class="slot" />

      <!-- walls, each one a thing you can take hold of -->
      <g v-for="w in wallsD" :key="w.id" :class="['wall', { on: w.carries, hot: hover?.kind === 'wall' && hover.id === w.id }]"
         @pointerdown.stop="grabWall($event, w)">
        <path :d="w.d" class="hit" />
        <path :d="w.line" class="line" />
        <g :transform="`translate(${w.lx} ${w.ly})`" class="dim">
          <rect :x="-w.lw / 2" y="-9" :width="w.lw" height="18" rx="9" />
          <text y="4">{{ w.mm }}</text>
        </g>
      </g>

      <!-- doors and windows, drawn as a plan draws them and draggable along their wall -->
      <g v-for="o in openingsD" :key="o.id" class="opening" :class="o.kind"
         @pointerdown.stop="grabOpening($event, o)">
        <path :d="`M${o.ax} ${o.ay}L${o.bx} ${o.by}`" class="hit" />
        <!-- A cased opening two metres wide has no leaf to swing, so it is drawn as what it
             is: the break in the wall, with a jamb tick at each end. Drawing a door and a
             2.4-metre arc there was how a doorway you can walk three abreast through came
             out looking like a cupboard door. -->
        <template v-if="o.kind === 'door' && !o.wide">
          <path :d="o.leaf" class="leaf" />
          <path :d="o.arc" class="arc" />
        </template>
        <template v-else-if="o.kind === 'door'">
          <path v-for="(p, i) in o.jambs" :key="i" :d="p" class="jamb" />
        </template>
        <template v-else>
          <path v-for="(p, i) in o.panes" :key="i" :d="p" class="pane" />
        </template>
      </g>

      <!-- corners -->
      <g v-for="v in vertsD" :key="'v' + v.i" class="vert" :class="{ hot: hover?.kind === 'vert' && hover.i === v.i }"
         @pointerdown.stop="grabVert($event, v)">
        <circle :cx="v.x" :cy="v.y" r="14" class="hit" />
        <circle :cx="v.x" :cy="v.y" r="5" class="dot" />
        <text v-if="v.deg !== 90" :x="v.x + v.tx" :y="v.y + v.ty" class="deg">{{ v.deg }}°</text>
      </g>

      <!-- which one is picked, in words, beside it -->
      <g v-if="tagD" class="tag" :transform="`translate(${tagD.x} ${tagD.y})`">
        <rect :x="-tagD.w / 2" y="-11" :width="tagD.w" height="22" rx="11" />
        <text y="4">{{ tagD.text }}</text>
      </g>

      <!-- a tape between two points -->
      <g v-if="points.length" class="tape">
        <circle v-for="(p, i) in pointsPx" :key="i" :cx="p.x" :cy="p.y" r="5" />
        <template v-if="tapeD">
          <path :d="tapeD.d" />
          <g :transform="`translate(${tapeD.lx} ${tapeD.ly})`">
            <rect :x="-tapeD.w / 2" y="-10" :width="tapeD.w" height="20" rx="10" />
            <text y="4">{{ tapeD.label }}</text>
          </g>
        </template>
      </g>
    </svg>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import * as room from '../kitchen/room.js';
import { offsets, slide } from '../kitchen/layout.js';
import { RULE } from '../kitchen/guidelines.js';

const props = defineProps({
  plan: Object, maker: Object, kitchen: Object, selected: Object, nudge: Number,
  /** Pixels of the bottom of the window the sheet and the bar are standing on. */
  inset: { type: Number, default: 0 },
  /** And of the top, where the brand bar is. */
  top: { type: Number, default: 0 },
  /**
   * WHICH WALLS ARE THE KITCHEN, while that is being asked -- the ids picked so far, walls
   * and islands alike. Null the rest of the time, which is the whole of ordinary editing.
   *
   * An empty array is not null: it means "asked, and nothing picked yet", which is a real
   * state and looks like a room with no kitchen in it.
   */
  picking: { type: Array, default: null },
});
const emit = defineEmits(['change', 'select', 'remember', 'committed', 'hint', 'toggle-wall', 'pick']);

/** The picked set, or null when nobody is picking. Every drag in this file asks it first. */
const picked = computed(() => (props.picking ? new Set(props.picking) : null));

const box = ref(null);
const size = ref({ w: 390, h: 480 });
const drag = ref(null);
const hover = ref(null);
const showClear = ref(false);
const measuring = ref(false);
const points = ref([]);   // in mm, up to two

function toggleMeasure() {
  measuring.value = !measuring.value;
  if (!measuring.value) points.value = [];
}

let ro;
onMounted(() => {
  ro = new ResizeObserver(() => {
    const r = box.value?.getBoundingClientRect();
    if (r && r.width && r.height) size.value = { w: Math.round(r.width), h: Math.round(r.height) };
  });
  ro.observe(box.value);
});
onBeforeUnmount(() => ro?.disconnect());

// ── mm <-> px ──────────────────────────────────────────────────────────────────────
//
// The room fits the box with a margin for dimension labels, and the scale is one number so
// that a cabinet drawn 600 wide is 600 in every direction. Recomputed when the walls move,
// which is why dragging a wall outward makes the whole plan shrink a little: the plan is
// always all of the room.
/** Appliances deeper than the run they stand in. In step with appliance.js's SIZE. */
const APPLIANCE_D = { fridge: 700 };
/** What to write on one, since at plan size it is otherwise a grey rectangle. */
const LETTER = { fridge: 'F', dishwasher: 'DW', oven: 'O' };

/** How far a corner unit reaches along the wall after this run, or 0 if there is none. */
function cornerReturn(plan, run, lim) {
  const w = room.segment(plan, run.wallId);
  if (!w || room.isIsland(plan, run.wallId)) return 0;
  const nextWall = room.next(plan.walls, w);
  if (!nextWall || Math.abs(room.corner(w, nextWall) - Math.PI / 2) > 0.09) return 0;
  if (!plan.runs.some((r) => r.kind === 'base' && r.wallId === nextWall.id)) return 0;
  return lim.cornerReturn ?? 0;
}

const PAD = 44;
const TOOLS_H = 44;   // the row of pills under the brand bar; the plan fits below it
const fit = computed(() => {
  props.nudge;
  const bb = room.bounds(props.plan.walls);
  const w = Math.max(bb.maxX - bb.minX, 1), h = Math.max(bb.maxY - bb.minY, 1);
  /*
   * Fit the room into what can be SEEN, not into the element.
   *
   * The plan is full-bleed under the sheet, so half of it was behind the sheet -- the room
   * was centred on a window whose bottom third was covered, and the dimension labels ran
   * under it. The sheet says how much it is standing on; the plan fits above that, and
   * re-fits the moment the sheet is dragged.
   */
  // And below the brand bar, which the top wall and its corner handles used to sit under.
  const top = props.top + TOOLS_H;
  const usable = Math.max(160, size.value.h - props.inset - top);
  const s = Math.min((size.value.w - PAD * 2) / w, (usable - PAD * 2) / h);
  const ox = (size.value.w - w * s) / 2 - bb.minX * s;
  const oy = top + (usable - h * s) / 2 - bb.minY * s;
  return { s, ox, oy };
});
const px = (p) => ({ x: p.x * fit.value.s + fit.value.ox, y: p.y * fit.value.s + fit.value.oy });
const mm = (p) => ({ x: (p.x - fit.value.ox) / fit.value.s, y: (p.y - fit.value.oy) / fit.value.s });
const r1 = (n) => Math.round(n * 10) / 10;

// ── drawing ────────────────────────────────────────────────────────────────────────

const grid = computed(() => {
  const step = 500 * fit.value.s;
  if (step < 12) return '';
  let d = '';
  const x0 = fit.value.ox % step, y0 = fit.value.oy % step;
  for (let x = x0; x <= size.value.w; x += step) d += `M${r1(x)} 0V${size.value.h}`;
  for (let y = y0; y <= size.value.h; y += step) d += `M0 ${r1(y)}H${size.value.w}`;
  return d;
});

const floorD = computed(() => {
  props.nudge;
  return room.vertices(props.plan.walls).map((v, i) => {
    const p = px(v);
    return (i ? 'L' : 'M') + r1(p.x) + ' ' + r1(p.y);
  }).join(' ') + ' Z';
});

const wallsD = computed(() => {
  props.nudge;
  /*
   * "On" means two different true things, and which one depends on what is being asked.
   *
   * Ordinarily a wall reads heavy because it HAS cabinets on it. While picking it reads
   * heavy because it MAY have -- the scan's runs are not drawn at all there, and a wall
   * lit by yesterday's cupboards while the question is about tomorrow's would be answering
   * the wrong one.
   */
  const carrying = picked.value ?? new Set(props.plan.runs.map((r) => r.wallId));
  return props.plan.walls.map((w) => {
    const a = px({ x: w.x1, y: w.y1 }), b = px({ x: w.x2, y: w.y2 });
    const len = Math.round(room.length(w) / 10) * 10;
    const mid = px(room.at(w, room.length(w) / 2));
    const n = room.inward(w);
    const label = len + ' mm';
    // Broken over its openings, which is how a plan shows one: the line stops at the door
    // and starts again after it. `hit` stays the whole wall, so it is still one thing to grab.
    const L = room.length(w);
    const cuts = (props.plan.openings ?? []).filter((o) => o.wallId === w.id)
      .map((o) => ({ from: Math.max(0, o.at), to: Math.min(L, o.at + o.width) }))
      .filter((o) => o.to > o.from).sort((p, q) => p.from - q.from);
    let at = 0;
    const parts = [];
    const seg = (s, e) => {
      if (e - s < 1) return;
      const p1 = px(room.at(w, s)), p2 = px(room.at(w, e));
      parts.push(`M${r1(p1.x)} ${r1(p1.y)} L${r1(p2.x)} ${r1(p2.y)}`);
    };
    for (const c of cuts) { seg(at, c.from); at = Math.max(at, c.to); }
    seg(at, L);
    return {
      id: w.id, carries: carrying.has(w.id),
      d: `M${r1(a.x)} ${r1(a.y)} L${r1(b.x)} ${r1(b.y)}`,
      line: parts.join(' ') || `M${r1(a.x)} ${r1(a.y)} L${r1(b.x)} ${r1(b.y)}`,
      mm: label, lw: label.length * 6.4 + 14,
      // the label sits just OUTSIDE the wall, where the tape would be
      lx: r1(mid.x - n.x * 18), ly: r1(mid.y - n.y * 18),
    };
  });
});

/**
 * Doors and windows, drawn the way a floor plan draws them.
 *
 * The wall stroke is broken over each one by `wallsD` above; this is the mark that goes in
 * the gap. A door gets a leaf and the arc it sweeps, a window a pair of thin lines. Both are
 * draggable along their wall, because the reader and the scan both put them approximately
 * and approximately is not where a doorway is.
 */
/** Wider than the widest door leaf anybody hangs: it is a cased opening, not a door. */
const WIDE_DOOR = 1200;

const openingsD = computed(() => {
  props.nudge;
  const out = [];
  for (const o of props.plan.openings ?? []) {
    const w = props.plan.walls.find((x) => x.id === o.wallId);
    if (!w) continue;
    const L = room.length(w);
    const from = Math.max(0, o.at), to = Math.min(L, o.at + o.width);
    if (to - from < 1) continue;
    const n = room.inward(w);
    const a = px(room.at(w, from)), b = px(room.at(w, to));
    const width = to - from;
    const item = { id: o.id, kind: o.kind, o, ax: r1(a.x), ay: r1(a.y), bx: r1(b.x), by: r1(b.y) };
    if (o.kind === 'window') {
      item.panes = [-30, 30].map((off) => {
        const p1 = px({ x: room.at(w, from).x + n.x * off, y: room.at(w, from).y + n.y * off });
        const p2 = px({ x: room.at(w, to).x + n.x * off, y: room.at(w, to).y + n.y * off });
        return `M${r1(p1.x)} ${r1(p1.y)}L${r1(p2.x)} ${r1(p2.y)}`;
      });
    } else if (width > WIDE_DOOR) {
      // A cased opening: two short ticks square to the wall, one at each jamb.
      item.wide = true;
      item.jambs = [from, to].map((t) => {
        const p = room.at(w, t);
        const p1 = px({ x: p.x - n.x * 60, y: p.y - n.y * 60 });
        const p2 = px({ x: p.x + n.x * 60, y: p.y + n.y * 60 });
        return `M${r1(p1.x)} ${r1(p1.y)}L${r1(p2.x)} ${r1(p2.y)}`;
      });
    } else {
      const hinge = room.at(w, from);
      const leaf = px({ x: hinge.x + n.x * width, y: hinge.y + n.y * width });
      const r = r1(width * fit.value.s);
      item.leaf = `M${r1(a.x)} ${r1(a.y)}L${r1(leaf.x)} ${r1(leaf.y)}`;
      item.arc = `M${r1(leaf.x)} ${r1(leaf.y)}A${r} ${r} 0 0 1 ${r1(b.x)} ${r1(b.y)}`;
    }
    const mid = px(room.at(w, (from + to) / 2));
    item.lx = r1(mid.x - n.x * 26 * fit.value.s / fit.value.s);
    item.cx = r1(mid.x); item.cy = r1(mid.y);
    item.label = `${Math.round(width)}`;
    out.push(item);
  }
  return out;
});

/**
 * An island, drawn: the cabinets' footprint and the seating overhang behind it.
 *
 * The overhang is on the side the cabinets are NOT, which is the segment's own outward
 * side. That is the whole of "which way does an island face" -- there is no inside of a room
 * for it to face, so its direction decides, and the Flip button swaps its ends.
 */
const islandsD = computed(() => {
  props.nudge;
  const lim = props.maker.limits;
  const out = picked.value;
  return (props.plan.islands ?? []).map((s) => {
    const n = room.inward(s), L = room.length(s);
    // A strip along the island between two distances out from its own line: [0, depth] is
    // the cabinets, [-overhang, 0] is the seating side behind them.
    const strip = (a, b) => {
      const corner = (along, out) => px({
        x: room.at(s, along).x + n.x * out, y: room.at(s, along).y + n.y * out,
      });
      const pts = [corner(0, a), corner(L, a), corner(L, b), corner(0, b)];
      return 'M' + pts.map((q) => r1(q.x) + ' ' + r1(q.y)).join(' L') + ' Z';
    };
    // The turning grip sits off the far end, clear of the cabinets.
    const g = px({ x: room.at(s, L).x + room.dir(s).x * 190, y: room.at(s, L).y + room.dir(s).y * 190 });
    return {
      id: s.id, seg: s,
      body: strip(0, lim.baseDepth),
      over: strip(-(s.overhang ?? 0), 0),
      gx: r1(g.x), gy: r1(g.y),
      out: !!out && !out.has(s.id),
    };
  });
});

/**
 * A grab handle at each end of every base run.
 *
 * Drawn across the run rather than along it, so it reads as a stop rather than as another
 * cabinet, and offset a little into the room so it does not sit under the wall's own drag
 * area. Base runs only: an overhead follows the base run under it, and giving a person two
 * sets of ends to reconcile is two ways to make the same kitchen wrong.
 */
const endsD = computed(() => {
  props.nudge;
  const lim = props.maker.limits;
  const out = [];
  // Nothing to pull in while the question is which walls at all.
  if (picked.value) return out;
  for (const run of props.plan.runs.filter((r) => r.kind === 'base')) {
    const w = room.segment(props.plan, run.wallId);
    if (!w) continue;
    const n = room.inward(w);
    for (const [side, at] of [['from', run.from ?? 0], ['to', run.to ?? room.length(w)]]) {
      const p = room.at(w, at);
      const a = px({ x: p.x, y: p.y });
      const b = px({ x: p.x + n.x * lim.baseDepth, y: p.y + n.y * lim.baseDepth });
      out.push({ key: `${run.id}-${side}`, runId: run.id, side, at,
        x: r1((a.x + b.x) / 2), y: r1((a.y + b.y) / 2),
        d: `M${r1(a.x)} ${r1(a.y)}L${r1(b.x)} ${r1(b.y)}` });
    }
  }
  return out;
});

const vertsD = computed(() => {
  props.nudge;
  // A corner handle is a drag, and while picking there are none. The corners are still
  // drawn -- they are where the wall lines meet.
  if (picked.value) return [];
  const walls = props.plan.walls;
  return walls.map((w, i) => {
    const p = px({ x: w.x1, y: w.y1 });
    const before = room.prev(walls, w);
    const deg = Math.round(room.corner(before, w) * 180 / Math.PI);
    // the angle label goes into the corner, along the bisector
    const na = room.inward(before), nb = room.inward(w);
    const bx = na.x + nb.x, by = na.y + nb.y;
    const bl = Math.hypot(bx, by) || 1;
    return { i, x: r1(p.x), y: r1(p.y), deg, tx: r1(bx / bl * 26), ty: r1(by / bl * 26 + 4) };
  });
});

/** Every run as a strip, with its cabinets drawn along it. */
const runsD = computed(() => {
  props.nudge;
  /*
   * The cabinets the scan found are NOT drawn while walls are being picked.
   *
   * They are last week's kitchen and the question is next week's, and a wall drawn heavy
   * with a row of cupboards along it has already answered it. The wall line carries the
   * whole answer instead, which is the one thing on this screen a finger can change.
   */
  if (picked.value) return [];
  const lim = props.maker.limits;
  const specs = props.maker.cabinets;
  return props.plan.runs
    .filter((r) => r.kind !== 'wall')   // overheads sit above the plan; base and tall are the plan
    .map((run) => {
      const w = room.segment(props.plan, run.wallId);
      if (!w) return null;
      const n = room.inward(w);
      // The depth is per ITEM now, not per run: a fridge is 700 deep against a 560 bench and
      // sticks out into the room, which is the thing a plan is for saying.
      /** The six points of an L: along the wall, out to the return, back to the front. */
      const lPath = (wall, s, width, depth, R) => {
        const dd = room.dir(wall), nn = room.inward(wall);
        const at2 = (u, v) => px({ x: wall.x1 + dd.x * u + nn.x * v, y: wall.y1 + dd.y * u + nn.y * v });
        const pts = [at2(s, 0), at2(s + width, 0), at2(s + width, R),
          at2(s + width - depth, R), at2(s + width - depth, depth), at2(s, depth)];
        return 'M' + pts.map((q) => r1(q.x) + ' ' + r1(q.y)).join(' L') + ' Z';
      };
      const rect = (s, e, depth = lim.baseDepth) => {
        const a = room.at(w, s), b = room.at(w, e);
        const pts = [a, b, { x: b.x + n.x * depth, y: b.y + n.y * depth }, { x: a.x + n.x * depth, y: a.y + n.y * depth }]
          .map(px);
        return 'M' + pts.map((p) => r1(p.x) + ' ' + r1(p.y)).join(' L') + ' Z';
      };
      const at = offsets(run.items);
      const cabs = run.items.map((item, i) => {
        const spec = specs.find((c) => c.id === item.specId);
        const depth = APPLIANCE_D[spec?.role] ?? lim.baseDepth;
        const s = run.from + at[i], e = s + item.w;
        // An L only where the cabinet's far edge actually reaches the end of its run: the
        // generator aims one at the corner and findSpot bumps it along when that spot is
        // taken, so being a corner unit and being in the corner are different questions.
        const R = spec?.role === 'corner' && run.kind === 'base' && e >= run.to - 1
          ? cornerReturn(props.plan, run, lim) : 0;
        const c = room.at(w, (s + e) / 2);
        const centre = px({ x: c.x + n.x * depth * 0.55, y: c.y + n.y * depth * 0.55 });
        const rr = Math.min(item.w, depth) * 0.36 * fit.value.s;
        const d = room.dir(w);
        const rings = [[-0.22, -0.22], [0.22, -0.22], [-0.22, 0.22], [0.22, 0.22]].map(([u, v]) => ({
          x: r1(centre.x + (d.x * u * item.w + n.x * v * depth) * fit.value.s),
          y: r1(centre.y + (d.y * u * item.w + n.y * v * depth) * fit.value.s),
        }));
        return {
          id: item.id, role: item.filler ? 'filler' : (spec?.role ?? null),
          d: R > depth ? lPath(w, s, item.w, depth, R) : rect(s, e, depth),
          letter: LETTER[spec?.role] ?? null,
          locked: (props.plan.locks ?? []).some((l) => l.itemId === item.id),
          cx: r1(centre.x), cy: r1(centre.y), r: r1(rr), rings, run, index: i,
        };
      });
      return { id: run.id, kind: run.kind, d: rect(run.from, run.to), cabs, run, wall: w };
    })
    .filter(Boolean);
});

const cornersD = computed(() => {
  props.nudge;
  const lim = props.maker.limits;
  const walls = props.plan.walls;
  const out = [];
  if (picked.value) return out;
  for (const run of props.plan.runs.filter((r) => r.kind === 'base')) {
    const w = walls.find((x) => x.id === run.wallId);
    if (!w) continue;
    const before = room.prev(walls, w);
    if (!props.plan.runs.some((r) => r.kind === 'base' && r.wallId === before.id)) continue;
    const a = room.cornerClaim(lim, lim.baseDepth, room.corner(before, w));
    const n = room.inward(w), nb = room.inward(before);
    const v = { x: w.x1, y: w.y1 };
    const p1 = room.at(w, a);
    const p2 = room.at(before, room.length(before) - a);
    const inner = { x: v.x + (n.x + nb.x) * lim.baseDepth, y: v.y + (n.y + nb.y) * lim.baseDepth };
    const pts = [v, p1, inner, p2].map(px);
    out.push('M' + pts.map((p) => r1(p.x) + ' ' + r1(p.y)).join(' L') + ' Z');
  }
  return out;
});

/**
 * The metre in front of every bench.
 *
 * Drawn as a band from the bench front, RULE.walkway deep, so a galley that is too narrow
 * shows two bands overlapping before the review has to say so. The label sits in the band.
 */
const clearD = computed(() => {
  props.nudge;
  const lim = props.maker.limits;
  const front = lim.baseDepth + lim.benchtopOverhang;
  const out = [];
  if (picked.value) return out;
  for (const run of props.plan.runs.filter((r) => r.kind === 'base')) {
    const w = room.segment(props.plan, run.wallId);
    if (!w) continue;
    const n = room.inward(w);
    const a = room.at(w, run.from ?? 0), b = room.at(w, run.to ?? room.length(w));
    const pts = [
      { x: a.x + n.x * front, y: a.y + n.y * front }, { x: b.x + n.x * front, y: b.y + n.y * front },
      { x: b.x + n.x * (front + RULE.walkway), y: b.y + n.y * (front + RULE.walkway) },
      { x: a.x + n.x * (front + RULE.walkway), y: a.y + n.y * (front + RULE.walkway) },
    ].map(px);
    const mid = room.at(w, ((run.from ?? 0) + (run.to ?? room.length(w))) / 2);
    const lp = px({ x: mid.x + n.x * (front + RULE.walkway / 2), y: mid.y + n.y * (front + RULE.walkway / 2) });
    // The words run along the wall, so a side run's label does not lie across its dimension.
    const d = room.dir(w);
    let deg = Math.atan2(d.y, d.x) * 180 / Math.PI;
    if (deg > 90 || deg < -90) deg += 180;
    out.push({ d: 'M' + pts.map((p) => r1(p.x) + ' ' + r1(p.y)).join(' L') + ' Z', lx: r1(lp.x), ly: r1(lp.y), rot: Math.round(deg), label: `${RULE.walkway} to stand` });
  }
  return out;
});

/** The picked cabinet's name and width, drawn just in front of it. */
const tagD = computed(() => {
  props.nudge;
  const sel = props.selected;
  if (!sel) return null;
  for (const r of runsD.value) {
    const c = r.cabs.find((x) => x.id === sel.id);
    if (!c) continue;
    const spec = props.maker.cabinets.find((s) => s.id === sel.specId);
    const text = `${spec?.name ?? 'Filler'} · ${Math.round(sel.w)}`;
    const n = room.inward(r.wall);
    const depth = props.maker.limits.baseDepth;
    // Past the front of the run, into the room, where it covers nothing that can be grabbed.
    const p = px(mm({ x: c.cx, y: c.cy }));
    return { x: r1(p.x + n.x * depth * 0.75 * fit.value.s), y: r1(p.y + n.y * depth * 0.75 * fit.value.s + (n.y > 0.5 ? 14 : n.y < -0.5 ? -14 : 0)), w: text.length * 6.6 + 18, text };
  }
  return null;
});

const pointsPx = computed(() => points.value.map(px).map((p) => ({ x: r1(p.x), y: r1(p.y) })));
const tapeD = computed(() => {
  if (points.value.length < 2) return null;
  const [a, b] = points.value;
  const pa = px(a), pb = px(b);
  const d = Math.round(Math.hypot(b.x - a.x, b.y - a.y) / 10) * 10;
  const label = `${d} mm`;
  return { d: `M${r1(pa.x)} ${r1(pa.y)} L${r1(pb.x)} ${r1(pb.y)}`, lx: r1((pa.x + pb.x) / 2), ly: r1((pa.y + pb.y) / 2 - 14), w: label.length * 6.6 + 16, label };
});

// ── the finger ─────────────────────────────────────────────────────────────────────

function pointer(e) {
  const r = box.value.getBoundingClientRect();
  return mm({ x: e.clientX - r.left, y: e.clientY - r.top });
}

/** While measuring, every press is a point, whatever it lands on. Snapped to 10mm. */
function mark(e) {
  const p = pointer(e);
  const q = { x: Math.round(p.x / 10) * 10, y: Math.round(p.y / 10) * 10 };
  points.value = points.value.length >= 2 ? [q] : [...points.value, q];
  if (points.value.length === 2) emit('hint', tapeD.value?.label ?? null);
}

/**
 * A doorway or a window, slid along its own wall.
 *
 * Where a scan or a reader put one is approximately right, and approximately is not where a
 * door is. Snapped to 10mm and stopped at the ends of the wall, so it cannot be dragged off
 * into a corner that does not exist.
 */
function grabOpening(e, o) {
  if (picked.value) return;
  if (measuring.value) { mark(e); return; }
  drag.value = { kind: 'opening', id: o.id, moved: false, start: pointer(e), was: o.o.at };
}

/**
 * One end of a run, pulled in or pushed back out.
 *
 * The span it writes is clamped by `extents` against the wall and the doorways, so a drag
 * can shorten a run and can never lengthen it past what the room allows -- which is what
 * keeps "drag it anywhere" and "every design is buildable" both true.
 */
/** An island, moved bodily. Snapped to 25mm, which is fine enough to line up on. */
function grabIsland(e, s) {
  // While picking, an island is a thing to say yes or no to, not a thing to slide.
  if (picked.value) { emit('pick', s.id); return; }
  if (measuring.value) { mark(e); return; }
  drag.value = { kind: 'island', id: s.id, moved: false, start: pointer(e), was: { ...s.seg } };
}

/** And turned, about its own middle, in the same 15° steps a corner snaps to. */
function grabSpin(e, s) {
  if (picked.value) return;
  if (measuring.value) { mark(e); return; }
  const c = room.middle(s.seg);
  drag.value = { kind: 'spin', id: s.id, moved: false, was: { ...s.seg }, centre: c,
    from: Math.atan2(pointer(e).y - c.y, pointer(e).x - c.x) };
}

function grabEnd(e, end) {
  if (picked.value) return;
  if (measuring.value) { mark(e); return; }
  const run = props.plan.runs.find((r) => r.id === end.runId);
  const w = run && room.segment(props.plan, run.wallId);
  if (!run || !w) return;
  drag.value = { kind: 'span', key: end.key, runId: end.runId, side: end.side, moved: false,
    start: pointer(e), was: end.at, full: { from: 0, to: room.length(w) } };
}

function grabWall(e, w) {
  /*
   * ANSWERED ON THE WAY DOWN, while picking.
   *
   * Everywhere else in this file a tap is a drag that did not move, decided in `up`, because
   * everywhere else a wall might be about to slide. Here nothing can slide, and a wall that
   * lights up the instant a finger lands is the difference between a control that feels like
   * a switch and one that feels like it is thinking about it.
   */
  if (picked.value) { emit('pick', w.id); return; }
  if (measuring.value) { mark(e); return; }
  const wall = props.plan.walls.find((x) => x.id === w.id);
  drag.value = { kind: 'wall', id: w.id, moved: false, start: pointer(e), was: { ...wall } };
}
function grabVert(e, v) {
  if (picked.value) return;
  if (measuring.value) { mark(e); return; }
  drag.value = { kind: 'vert', i: v.i, moved: false, start: pointer(e) };
}
function grabCab(e, r, c) {
  if (picked.value) return;
  if (measuring.value) { mark(e); return; }
  const p = pointer(e);
  if ((props.plan.locks ?? []).some((l) => l.itemId === c.id)) {
    drag.value = { kind: 'cab', locked: true, runId: r.run.id, itemId: c.id, moved: false, start: p };
    return;
  }
  const along = alongWall(r.wall, p);
  const at = offsets(r.run.items)[c.index];
  drag.value = {
    kind: 'cab', runId: r.run.id, itemId: c.id, moved: false, start: p,
    grabbed: along - (r.run.from + at), before: r.run.items.slice(),
  };
}

function down(e) {
  // A press on the floor selects nothing; while measuring it is a point.
  if (picked.value) return;
  if (measuring.value) mark(e);
}

/** Distance along a wall of the point on it nearest to p. */
function alongWall(w, p) {
  const d = room.dir(w);
  return (p.x - w.x1) * d.x + (p.y - w.y1) * d.y;
}

function move(e) {
  const p = pointer(e);
  if (!drag.value) {
    // hover, for the cursor
    return;
  }
  const g = drag.value;
  const dx = p.x - g.start.x, dy = p.y - g.start.y;
  if (!g.moved && Math.hypot(dx, dy) * fit.value.s < 6) return;
  if (!g.moved) { g.moved = true; if (g.kind !== 'cab') emit('remember'); else if (!g.crossed) emit('remember', g.runId); }

  if (g.kind === 'wall') {
    /*
     * A wall slides along its own inward normal, and only that: the two walls it meets
     * simply get longer or shorter. Snapped to 50mm, because nobody's kitchen is 3652.
     */
    const walls = props.plan.walls.map((w) => ({ ...w }));
    const w = walls.find((x) => x.id === g.id);
    const n = room.inward(w);
    const t = Math.round((dx * n.x + dy * n.y) / 50) * 50;
    w.x1 = g.was.x1 + n.x * t; w.y1 = g.was.y1 + n.y * t;
    w.x2 = g.was.x2 + n.x * t; w.y2 = g.was.y2 + n.y * t;
    const i = walls.indexOf(w);
    const before = walls[(i - 1 + walls.length) % walls.length];
    const after = walls[(i + 1) % walls.length];
    before.x2 = w.x1; before.y2 = w.y1;
    after.x1 = w.x2; after.y1 = w.y2;
    props.kitchen.setWalls(walls);
    emit('hint', `${Math.round(room.length(w) / 10) * 10} mm`);
    emit('change');
  }

  if (g.kind === 'vert') {
    /*
     * A corner moves freely, then snaps: the position to 50mm, and each of the two walls it
     * belongs to onto a clean angle when it is within a few degrees of one. That is how a
     * splayed corner gets made -- drag the corner in until the wall reads 135.
     */
    const walls = props.plan.walls.map((w) => ({ ...w }));
    const i = g.i;
    const w = walls[i];                                  // starts at this vertex
    const before = walls[(i - 1 + walls.length) % walls.length];   // ends at it
    /*
     * Snap the INCOMING wall to a clean angle, always -- fifteen-degree steps, so a splay
     * reads 135 and not 131 -- and keep its length to the nearest 50. The outgoing wall then
     * follows wherever that puts the corner. Snapping only when "close enough" gave 114 and
     * 81, which is not a corner anybody would build.
     */
    const step = Math.PI / 12;
    const ax = before.x1, ay = before.y1;
    const raw = Math.atan2(p.y - ay, p.x - ax);
    const ang = Math.round(raw / step) * step;
    const len = Math.round(Math.hypot(p.x - ax, p.y - ay) / 50) * 50;
    let vx = ax + Math.cos(ang) * len, vy = ay + Math.sin(ang) * len;
    // An axis-aligned incoming wall lands on whole millimetres; a diagonal one does not,
    // and the outgoing wall's far end is where the room's other numbers live -- round the
    // vertex itself so the loop stays on integers.
    vx = Math.round(vx); vy = Math.round(vy);
    w.x1 = vx; w.y1 = vy;
    before.x2 = vx; before.y2 = vy;
    props.kitchen.setWalls(walls);
    const deg = Math.round(room.corner(before, w) * 180 / Math.PI);
    emit('hint', deg === 90 ? 'Square' : `${deg}° corner`);
    emit('change');
  }

  if (g.kind === 'island' || g.kind === 'spin') {
    const list = (props.plan.islands ?? []).slice();
    const i = list.findIndex((s) => s.id === g.id);
    if (i < 0) return;
    if (g.kind === 'island') {
      const dx = Math.round((p.x - g.start.x) / 25) * 25;
      const dy = Math.round((p.y - g.start.y) / 25) * 25;
      list[i] = room.moveSegment(g.was, dx, dy);
      emit('hint', 'Island');
    } else {
      const now = Math.atan2(p.y - g.centre.y, p.x - g.centre.x);
      const deg = Math.round(((now - g.from) * 180 / Math.PI) / 15) * 15;
      list[i] = room.spinSegment(g.was, -deg);
      emit('hint', `${((Math.round(-deg) % 360) + 360) % 360}°`);
    }
    props.kitchen.setIslands(list);
    emit('change');
    return;
  }

  if (g.kind === 'span') {
    const run = props.plan.runs.find((r) => r.id === g.runId);
    const w = run && room.segment(props.plan, run.wallId);
    if (!run || !w) return;
    const d = room.dir(w);
    const along = (p.x - g.start.x) * d.x + (p.y - g.start.y) * d.y;
    const at = Math.round((g.was + along) / 10) * 10;
    const span = { from: run.from ?? g.full.from, to: run.to ?? g.full.to, ...(run.span ?? {}) };
    if (g.side === 'from') span.from = Math.min(at, span.to - room.MIN_RUN);
    else span.to = Math.max(at, span.from + room.MIN_RUN);
    // A run pulled back out to its wall stops being an override, so it follows the wall
    // again when the room is resized. Storing "the whole wall" as a number would freeze it.
    const whole = span.from <= g.full.from + 5 && span.to >= g.full.to - 5;
    run.span = whole ? undefined : span;
    props.kitchen.setWalls(props.plan.walls);
    emit('hint', `${Math.round((run.to ?? 0) - (run.from ?? 0))} mm of cabinets on the ${WALL_WORD[run.wallId] ?? 'wall'}`);
    emit('change');
    return;
  }

  if (g.kind === 'opening') {
    const o = (props.plan.openings ?? []).find((x) => x.id === g.id);
    const w = o && props.plan.walls.find((x) => x.id === o.wallId);
    if (!o || !w) return;
    const d = room.dir(w);
    const along = (p.x - g.start.x) * d.x + (p.y - g.start.y) * d.y;
    const L = room.length(w);
    o.at = Math.max(0, Math.min(Math.round((g.was + along) / 10) * 10, L - o.width));
    props.kitchen.setOpenings(props.plan.openings);
    emit('hint', `${o.kind === 'door' ? 'Doorway' : 'Window'} ${Math.round(o.at)} mm along the ${WALL_WORD[o.wallId] ?? 'wall'}`);
    emit('change');
    return;
  }

  if (g.kind === 'cab') {
    if (g.locked) { emit('hint', 'Locked in place. Tap it to unlock.'); return; }
    const run = props.plan.runs.find((r) => r.id === g.runId);
    if (!run) return;
    const item = run.items.find((it) => it.id === g.itemId);
    if (!item) return;
    /*
     * Nearest leg of the rail wins, same as in 3D: the finger's distance from each leg's
     * wall line, and the leg it is closest to is where the cabinet goes.
     */
    const { chain } = room.chainOf(props.plan, run);
    let best = null;
    for (const leg of chain.legs) {
      const w = room.segment(props.plan, leg.wallId);
      const along = alongWall(w, p);
      const on = room.at(w, along);
      const n = room.inward(w);
      // measured from the run's centreline, not the wall, so a finger in the room is fair
      const gap = Math.abs((p.x - on.x) * n.x + (p.y - on.y) * n.y - props.maker.limits.baseDepth / 2)
        + Math.max(0, -along, along - room.length(w));
      if (!best || gap < best.gap) best = { leg, w, along, gap };
    }
    if (best.leg !== run) {
      if (!g.crossed) { emit('remember'); g.crossed = true; }
      const x = Math.min(Math.max(best.along - g.grabbed, best.leg.from), best.leg.to - item.w) - best.leg.from;
      const r = props.kitchen.moveAcross(item.id, run, best.leg, x);
      if (!r.ok) { emit('hint', r.why); return; }
      g.runId = r.run.id;
      emit('hint', r.said ?? 'Round the corner');
      emit('change');
      return;
    }
    const along = alongWall(best.w, p) - g.grabbed;
    const i = run.items.findIndex((it) => it.id === g.itemId);
    const next = slide(run.items, i, along - run.from);
    if (next.findIndex((it) => it.id === g.itemId) !== i) {
      run.items = next;
      props.kitchen.buildRuns();
      emit('change');
    }
    // Where it is now, in numbers: the width and how far along the wall its near edge sits.
    const k = run.items.findIndex((it) => it.id === g.itemId);
    const at = offsets(run.items)[k];
    const spec = props.maker.cabinets.find((s) => s.id === item.specId);
    emit('hint', `${spec?.name ?? 'Filler'} · ${Math.round(item.w)} wide · ${Math.round(run.from + at)} mm along the ${WALL_WORD[run.wallId] ?? 'wall'}`);
  }
}

const WALL_WORD = { back: 'back wall', left: 'left wall', right: 'right wall', front: 'facing wall' };

function up() {
  const g = drag.value;
  drag.value = null;
  emit('hint', null);
  if (!g) return;
  if (!g.moved) {
    // A tap.
    if (g.kind === 'wall') toggleWall(g.id);
    if (g.kind === 'cab') {
      const run = props.plan.runs.find((r) => r.id === g.runId);
      const item = run?.items.find((it) => it.id === g.itemId);
      if (item) emit('select', item, run);
    }
    return;
  }
  if (g.kind === 'cab' && !g.crossed) emit('committed', g.runId, g.before);
}

/**
 * Cabinets on, cabinets off. The page owns the plan, so the page does it; this only asks.
 * A wall with nothing on it gets a base run and overheads; a wall with runs loses them. That
 * is the whole shape control: one wall, galley, L and U are just which walls are on.
 */
function toggleWall(wallId) { emit('toggle-wall', wallId); }
</script>

<style scoped>
/* Opaque: it sits over the 3D canvas, and a plan with a kitchen showing through it reads as
   a bug rather than a view. */
.plan { position: absolute; inset: 0; touch-action: none; background: var(--k-room); z-index: 1; }
svg { display: block; user-select: none; -webkit-user-select: none; }
svg.measuring { cursor: crosshair; }

.tools {
  position: absolute; left: 12px; z-index: 2; display: flex; gap: 6px;
}
.tools button {
  border: 0; border-radius: 999px; cursor: pointer; padding: 8px 12px; min-height: 34px;
  background: color-mix(in oklab, var(--k-paper) 90%, transparent); color: var(--k-dim);
  font: 500 12px/1 var(--ion-font-family, system-ui);
  box-shadow: 0 1px 3px rgba(0,0,0,.10);
}
.tools button.on { background: var(--k-ink); color: var(--k-paper); }

.clear path { fill: var(--k-accent); opacity: .10; stroke: var(--k-accent); stroke-width: 1; stroke-dasharray: 5 4; pointer-events: none; }
.clear text { text-anchor: middle; font: 500 10.5px/1 var(--ion-font-family, system-ui); fill: var(--k-accent); pointer-events: none; }

.tag rect { fill: var(--k-ink); }
.tag text { text-anchor: middle; font: 600 11px/1 var(--ion-font-family, system-ui); fill: var(--k-paper); }
.tag { pointer-events: none; }

.tape { pointer-events: none; }
.tape circle { fill: var(--k-paper); stroke: var(--k-accent); stroke-width: 2.5; }
.tape path { stroke: var(--k-accent); stroke-width: 2; stroke-dasharray: 6 4; fill: none; }
.tape rect { fill: var(--k-accent); }
.tape text { text-anchor: middle; font: 600 11px/1 var(--ion-font-family, system-ui); fill: var(--k-paper); }

.grid { stroke: var(--k-ink); stroke-width: .6; opacity: .05; }
.floor { fill: color-mix(in oklab, var(--k-paper) 70%, var(--k-room)); }

.run .strip { fill: color-mix(in oklab, var(--k-ink) 6%, var(--k-paper)); stroke: none; }
.cab path { fill: #93A08C; stroke: var(--k-paper); stroke-width: 1.5; stroke-linejoin: round; cursor: grab; }
.cab:nth-child(even) path { fill: #8E9B87; }
.cab.on path { stroke: var(--k-ink); stroke-width: 3; fill: var(--k-accent); }
.cab.held path { fill: #4A4A4C; }
.cab .sink { fill: #B9BEC2; stroke: none; }
.cab .hob circle { fill: #2B2B2E; stroke: none; }
.cab .letter { text-anchor: middle; font: 600 11px/1 var(--ion-font-family, system-ui); fill: #F7F3EC; pointer-events: none; }
.cab path.corner { fill: none; stroke: var(--k-accent); stroke-width: 1.2; stroke-dasharray: 3 3; }
.cab .lock rect { fill: var(--k-paper); stroke: var(--k-ink); stroke-width: 1.3; }
.cab .lock path { stroke: var(--k-ink); stroke-width: 1.3; }

.slot { fill: none; stroke: var(--k-accent); stroke-width: 1.2; stroke-dasharray: 4 4; opacity: .7; pointer-events: none; }

.wall .hit { stroke: transparent; stroke-width: 22; fill: none; cursor: grab; }
.wall .line { stroke: var(--k-ink); stroke-width: 2.5; opacity: .28; fill: none; pointer-events: none; stroke-linecap: square; }
.wall.on .line { opacity: .8; stroke-width: 3.5; }
.wall.hot .line { stroke: var(--k-accent); opacity: 1; }
.dim rect { fill: var(--k-paper); }
.dim text { text-anchor: middle; font: 600 11px/1 var(--ion-font-family, system-ui); fill: var(--k-dim); }
.wall.on .dim text { fill: var(--k-ink); }

/* ── while the kitchen's walls are being picked ───────────────────────────────
   IN or OUT, and the difference has to be readable at arm's length on a phone held
   over a bench. A wall that is in is the heaviest line on the screen, in the ink
   colour; one that is out is a hairline in the muted one -- still drawn, still
   measured, still carrying its doorways and windows, because it is still the room.

   The millimetre labels go with the walls that are out. Twenty of them at once on a
   390-wide screen is not information, and the number a person wants is how long the
   walls they have CHOSEN are. */
.picking .wall .hit { cursor: pointer; }
.picking .wall .line { stroke: var(--k-mute); stroke-width: 1.2; opacity: .5; }
.picking .wall.on .line { stroke: var(--k-ink); stroke-width: 7; opacity: 1; stroke-linecap: butt; }
.picking .wall .dim { display: none; }
.picking .wall.on .dim { display: inline; }
.picking .wall.on .dim text { fill: var(--k-ink); }
/* An island in is a solid block of cabinetry; an island out is its outline, dashed,
   in the same hand as a wall that is out. */
.picking .island .body {
  fill: color-mix(in oklab, var(--k-ink) 38%, var(--k-paper));
  stroke: var(--k-ink); stroke-width: 2; cursor: pointer;
}
.picking .island.out .body { fill: none; stroke: var(--k-mute); stroke-width: 1.2; stroke-dasharray: 5 4; }
.picking .island.out .over { display: none; }

.island .body { fill: color-mix(in oklab, var(--k-ink) 6%, var(--k-paper)); stroke: none; cursor: move; }
.island .over { fill: var(--k-accent); opacity: .10; stroke: var(--k-accent); stroke-width: 1; stroke-dasharray: 5 4; }
.island .spin .hit { fill: transparent; cursor: grab; }
.island .spin .dot { fill: var(--k-paper); stroke: var(--k-accent); stroke-width: 2.5; pointer-events: none; }

.runend .hit { fill: transparent; cursor: ew-resize; }
.runend .bar { stroke: var(--k-ink); stroke-width: 3; opacity: .3; stroke-linecap: round; pointer-events: none; }
.runend.hot .bar, .runend:active .bar { stroke: var(--k-accent); opacity: 1; }

.opening .hit { stroke: transparent; stroke-width: 20; fill: none; cursor: grab; }
.opening .leaf { stroke: var(--k-ink); stroke-width: 2; fill: none; opacity: .6; }
.opening .arc { stroke: var(--k-ink); stroke-width: 1; fill: none; opacity: .28; stroke-dasharray: 5 4; }
.opening .jamb { stroke: var(--k-ink); stroke-width: 2; fill: none; opacity: .6; }
.opening .pane { stroke: var(--k-ink); stroke-width: 1.2; fill: none; opacity: .5; }

.vert .hit { fill: transparent; cursor: move; }
.vert .dot { fill: var(--k-paper); stroke: var(--k-ink); stroke-width: 2; pointer-events: none; }
.vert.hot .dot { stroke: var(--k-accent); }
.vert .deg { text-anchor: middle; font: 600 10.5px/1 var(--ion-font-family, system-ui); fill: var(--k-accent); pointer-events: none; }
</style>
